<template>
  <BuilderWrapper
    title="Tabs"
    helpful-tip="Choose colour, icon and label for your tabs."
  >
    <b-form-group class="mb-2">
      <b-checkbox
        v-model="showIcon"
        data-test="show-icon"
      >
        Show Icon
      </b-checkbox>
      <b-checkbox
        v-model="showLabel"
        data-test="show-label"
      >
        Show Label
      </b-checkbox>
      <b-checkbox
        v-model="customiseTabFontColour"
        data-test="customise-tab-font-colour"
      >
        Customise font colours
      </b-checkbox>
      <b-checkbox
        v-if="showHideSubrootTab"
        v-model="hideSubrootTab"
        data-test="hide-subroot-tab"
      >
        Hide subroot tab
      </b-checkbox>
    </b-form-group>

    <ReorderableList
      :items.sync="reorderableTabs"
      :selected.sync="selectedTabPath"
      class="mb-2"
    />

    <b-button
      variant="primary"
      block
      data-test="add-custom-tab"
      @click="addCustomTab"
    >
      Add Custom Tab
    </b-button>

    <b-button
      v-if="isConsolidatedApp"
      variant="primary"
      block
      data-test="add-secondary-app"
      @click="addSecondaryApp"
    >
      Add App
    </b-button>

    <hr class="mt-4">

    <SecondaryAppSettings
      v-if="secondaryAppTab"
      data-test="select-secondary-apps"
      :tab="selectedTab"
    />

    <TabIconColourSetting
      v-else-if="selectedTab"
      :tab="selectedTab"
    />
  </BuilderWrapper>
</template>

<script lang="ts">

import Vue from 'vue'
import Component from 'vue-class-component'

import BuilderWrapper from '@/components/builder/UI/BuilderWrapper.vue'
import ReorderableList from '@/components/common/ReorderableList.vue'
import TabIconColourSetting from '@/components/builder/TabSettings/TabIconColourSetting.vue'
import SecondaryAppSettings from '@/components/builder/TabSettings/SecondaryAppSettings.vue'

import { getTabLabel } from '@/components/builder/helpers'
import { AppConfig, NodeTypes } from '@/types'
import { APP_CONFIG_KEY, NODE_CONFIG_KEY } from '@/constants'

const components = {
  BuilderWrapper,
  ReorderableList,
  TabIconColourSetting,
  SecondaryAppSettings,
}

@Component({ components })
export default class TabSettings extends Vue {
  selectedPath: string | null = null

  get tabs () {
    return this.$typedStore.primary.entities.tabs.builderTabs
  }

  get reorderableTabs () {
    return this.tabs.map(tab => {
      const label = getTabLabel(tab)
      return { key: tab.path, ...tab, label }
    })
  }

  set reorderableTabs (reorderedTabs) {
    reorderedTabs.forEach((tab, index) => {
      this.$typedStore.primary.entities.nodes.updateNodeProp({
        path: tab.path,
        configPath: [NODE_CONFIG_KEY, 'order'],
        value: index,
      })
    })
  }

  get selectedTabPath () {
    return this.selectedPath || this.tabs[0]?.path
  }

  set selectedTabPath (path: string) {
    this.selectedPath = path
  }

  get selectedTab () {
    return this.tabs.find(x => x.path === this.selectedTabPath)
  }

  get showLabel () {
    return this.$typedStore.primary.app.config.appConfig?.showLabel ?? true
  }

  set showLabel (value) {
    this.updateAppConfigProp(value, 'showLabel')
  }

  get showIcon () {
    return this.$typedStore.primary.app.config.appConfig?.showIcon ?? true
  }

  set showIcon (value) {
    this.updateAppConfigProp(value, 'showIcon')
  }

  get customiseTabFontColour () {
    return this.$typedStore.primary.app.config.appConfig?.customiseTabFontColour ?? false
  }

  set customiseTabFontColour (value) {
    this.updateAppConfigProp(value, 'customiseTabFontColour')
  }

  get showHideSubrootTab () {
    return !this.$typedStore.primary.app.isConsolidatedApp
  }

  get hideSubrootTab () {
    return this.$typedStore.primary.app.config.appConfig?.hideSubrootTab ?? false
  }

  set hideSubrootTab (value) {
    this.updateAppConfigProp(value, 'hideSubrootTab')
  }

  get isConsolidatedApp () {
    return this.$typedStore.primary.app.isConsolidatedApp
  }

  get secondaryAppTab () {
    return this.selectedTab?.nodeType === NodeTypes.APP
  }

  addSecondaryApp () {
    this.$typedStore.primary.entities.nodes.addSecondaryAppReferenceNode()
  }

  addCustomTab () {
    this.$typedStore.primary.entities.tabs.addCustomTab()
  }

  updateAppConfigProp (value: boolean, ...args: [keyof AppConfig] | []) {
    this.$typedStore.primary.app.updateConfig({
      configPath: [APP_CONFIG_KEY, ...args],
      value,
    })
  }
}

</script>
